import {
  faCheckCircle,
  faListAlt,
  faListUl
} from '@fortawesome/free-solid-svg-icons';
import { faShareSquare, faTimes } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button } from 'components/shared/Button';
import ButtonLink from 'components/shared/ButtonLink.styled';
import { TrimmedOfferLocations } from 'components/TrimmedOfferLocations';
import { LoggedUserContext } from 'context/logged-user/loggeduser.context';
import { DEFAULT_FILTER } from 'features/job-offers/list/components/JobOffersListComponent.constants';
import { useI18n } from 'hooks/useI18n';
import { UIOfferBottomBar } from 'interfaces/ui/ui-offer-bottombar';
import { useRouter } from 'next/dist/client/router';
import Link from 'next/link';
import { useContext, useEffect, useRef, useState } from 'react';
import ServiceEventTracking from 'services/service.event-tracking';
import { APP_URLS, getAppUrl } from 'utils/app-urls';
import { getUrlParamsFromJobOffersFilter } from 'utils/job-offers-filter';
import { getLocale } from 'utils/localize.utils';
import { Badge } from '../../shared/Badge';
import {
  StyledOfferBottomBar,
  StyledOfferBottomBarActions,
  StyledOfferBottomBarContainer,
  StyledOfferBottomBarLocation,
  StyledOfferBottomBarLogo,
  StyledOfferBottomBarNavTrigger,
  StyledOfferBottomBarPageNav,
  StyledOfferBottomBarPosition,
  StyledOfferBottomBarPositionLocation,
  StyledOfferBottomBarSalary,
  StyledOfferBottomBarSalaryGroup,
  StyledOfferBottomBarSalaryTitle,
  StyledOfferBottomBarSalaryValue,
  StyledOfferBottomBarUserApplied
} from './OfferBottomBar.styled';
import OfferBottomBarPageNav from './OfferBottomBarPageNav';

const OfferBottomBar: React.FC<UIOfferBottomBar> = (props) => {
  const { t } = useI18n('job-offer');
  const { locale } = useRouter();

  const { user, loadingUser } = useContext(LoggedUserContext);

  const nav = useRef<HTMLDivElement>(null);
  const bottomBarRef = useRef<HTMLDivElement>(null);

  const [setActive, setActiveState] = useState('');
  const [setAttached, setAttachedState] = useState(false);
  const [isScroll, setScrollState] = useState(false);

  const hasLocations = props.jobLocationsSummary.trimmedCount > 0;

  const salaryMin = props.salaryMin || null;
  const salaryMax = props.salaryMax || null;

  const returnUrl = `/${locale}${getAppUrl(
    APP_URLS.portalV2Dashboard,
    locale
  )}`;

  const trackApplyClick = (): void => {
    ServiceEventTracking.track({
      type: 'Offers - Offer Interested',
      properties: {
        buttonName: 'sectionCTA',
        offerCompany: props.companyName,
        offerName: props.internalCode,
        offerURL: window.location.href,
        pageLanguage: locale
      }
    });
  };

  const togglePageNav = (): void => {
    setActiveState(setActive === '' ? 'is-active' : '');
  };

  const handleScroll = (): void => {
    let bottomBarPosition = 0;

    setScrollState(true);

    if (bottomBarRef?.current) {
      bottomBarPosition = bottomBarRef.current.getBoundingClientRect().bottom;
    }

    setAttachedState(window.innerHeight > bottomBarPosition + 150);
  };

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);

    return () => window.removeEventListener('scroll', handleScroll);
  });

  return (
    <StyledOfferBottomBar
      id="bottom-bar"
      ref={bottomBarRef}
      className={`${setAttached ? 'is-detached' : ''} ${
        isScroll ? 'is-scroll' : ''
      } ${props.closed ? 'is-offer-closed' : ''}`}
    >
      <StyledOfferBottomBarContainer>
        <StyledOfferBottomBarNavTrigger type="button" onClick={togglePageNav}>
          {setActive === 'is-active' && <FontAwesomeIcon icon={faTimes} />}
          {setActive !== 'is-active' && <FontAwesomeIcon icon={faListAlt} />}
          <span>{t('footer.bottomBar.menu')}</span>
        </StyledOfferBottomBarNavTrigger>
        <StyledOfferBottomBarLogo
          src={props.companyLogo}
          alt={props.companyName}
        />
        <StyledOfferBottomBarPositionLocation>
          <StyledOfferBottomBarPosition>
            {props.jobTitle}
          </StyledOfferBottomBarPosition>
          <StyledOfferBottomBarLocation>
            {props.jobRemote === 100 ? (
              <>
                {props.jobRemote}%{' '}
                {props.jobRemote === 100
                  ? t('footer.remote')
                  : t('footer.remotework')}
                {hasLocations &&
                  typeof props.jobRemote !== 'undefined' &&
                  props.jobRemote < 100 &&
                  props.jobRemote > 0 && <> {t('footer.and')} </>}
                {hasLocations &&
                  typeof props.jobRemote !== 'undefined' &&
                  props.jobRemote === 100 && <> {t('footer.or')} </>}
                {hasLocations && (
                  <TrimmedOfferLocations
                    locationsSummary={props.jobLocationsSummary}
                  />
                )}
              </>
            ) : (
              <>
                {hasLocations && (
                  <TrimmedOfferLocations
                    locationsSummary={props.jobLocationsSummary}
                  />
                )}

                {hasLocations &&
                  typeof props.jobRemote !== 'undefined' &&
                  props.jobRemote !== 0 &&
                  props.jobRemote < 100 &&
                  props.jobRemote > 0 && <> {t('footer.and')} </>}

                {hasLocations &&
                  typeof props.jobRemote !== 'undefined' &&
                  props.jobRemote !== 0 &&
                  props.jobRemote === 100 && <> {t('footer.or')} </>}

                {props.jobRemote !== 0 && (
                  <>
                    {props.jobRemote}%{' '}
                    {props.jobRemote === 100
                      ? t('footer.remote')
                      : t('footer.remotework')}
                  </>
                )}
              </>
            )}
          </StyledOfferBottomBarLocation>
        </StyledOfferBottomBarPositionLocation>
        <StyledOfferBottomBarSalaryGroup>
          <StyledOfferBottomBarSalary>
            <StyledOfferBottomBarSalaryTitle>
              {props.isFreelance ? t('footer.fee') : t('footer.salary')}
            </StyledOfferBottomBarSalaryTitle>
            {props.isFreelance ? (
              <StyledOfferBottomBarSalaryValue>
                <strong>
                  {!!props.hourlyRateTo && !props.hourlyRateFrom && (
                    <span>{t('footer.up_to')} </span>
                  )}
                  {!!props.hourlyRateFrom && !props.hourlyRateTo && (
                    <span>{t('footer.from')} </span>
                  )}
                  {!!props.hourlyRateFrom &&
                    props.hourlyRateFrom !== props.hourlyRateTo &&
                    props.hourlyRateFrom}
                  {!!props.hourlyRateFrom &&
                    props.hourlyRateFrom !== props.hourlyRateTo &&
                    !!props.hourlyRateTo && <span>-</span>}
                  {!!props.hourlyRateTo && props.hourlyRateTo}
                  {props.currency}
                </strong>
                <span>/{t('common:salary.per_hour')}</span>
              </StyledOfferBottomBarSalaryValue>
            ) : (
              <StyledOfferBottomBarSalaryValue>
                {!salaryMin && salaryMax && <span>{t('footer.up_to')} </span>}

                {salaryMin && !salaryMax && <span>{t('footer.from')} </span>}

                <span>{props.currency}</span>

                {salaryMin && salaryMax && salaryMin !== salaryMax && (
                  <>
                    {salaryMin} <span>-</span> {salaryMax}
                  </>
                )}

                {salaryMin && salaryMax && salaryMin === salaryMax && (
                  <>{salaryMax}</>
                )}

                {!salaryMin && salaryMax && <strong>{salaryMax}</strong>}

                {salaryMin && !salaryMax && <strong>{salaryMin}</strong>}

                <span>K</span>
              </StyledOfferBottomBarSalaryValue>
            )}
          </StyledOfferBottomBarSalary>

          {props.hasBonus && (
            <StyledOfferBottomBarSalary>
              <StyledOfferBottomBarSalaryTitle>
                {t('footer.bonus')}
              </StyledOfferBottomBarSalaryTitle>
              <StyledOfferBottomBarSalaryValue>
                <span className="extra">+</span>
                <span>{props.currency}</span>
                <>{props.bonus}</>
                <span>K</span>
              </StyledOfferBottomBarSalaryValue>
            </StyledOfferBottomBarSalary>
          )}

          {props.hasEquity && (
            <StyledOfferBottomBarSalary>
              <StyledOfferBottomBarSalaryTitle>
                {t('footer.equity')}
              </StyledOfferBottomBarSalaryTitle>
              <StyledOfferBottomBarSalaryValue>
                {!props.equityInf && props.equitySup && (
                  <span className="extra">{t('offer.up_to')} </span>
                )}
                {props.equityInf && !props.equitySup && (
                  <span className="extra">{t('offer.from')} </span>
                )}
                {props.equityInf &&
                  props.equitySup &&
                  props.equityInf !== props.equitySup && (
                    <>
                      <strong>{props.equityInf}</strong>
                      <span>%</span>
                      <span> - </span>
                      <strong>{props.equitySup}</strong>
                      <span>%</span>
                    </>
                  )}
                {props.equityInf &&
                  props.equitySup &&
                  props.equityInf === props.equitySup && (
                    <>
                      <strong>{props.equitySup}</strong>
                      <span>%</span>
                    </>
                  )}
                {!props.equityInf && props.equitySup && (
                  <>
                    <strong>{props.equitySup}</strong>
                    <span>%</span>
                  </>
                )}
                {props.equityInf && !props.equitySup && (
                  <>
                    <strong>{props.equityInf}</strong>
                    <span>%</span>
                  </>
                )}
              </StyledOfferBottomBarSalaryValue>
            </StyledOfferBottomBarSalary>
          )}
        </StyledOfferBottomBarSalaryGroup>
        <StyledOfferBottomBarActions>
          {props.closed && (
            <>
              <Badge variant="negative">
                {t('footer.bottomBar.closedOffer')}
              </Badge>
              {!props.userApplied && (
                <Link
                  href={`${getAppUrl(
                    APP_URLS.jobOffers,
                    locale
                  )}?${getUrlParamsFromJobOffersFilter(
                    DEFAULT_FILTER,
                    getLocale(locale)
                  )}`}
                  passHref
                >
                  <ButtonLink
                    variant="secondaryInverse"
                    sizes="sm"
                    aria-label={t('footer.bottomBar.otherOffers')}
                  >
                    <FontAwesomeIcon icon={faListUl} />
                    <span aria-hidden="true">
                      {t('footer.bottomBar.otherOffers')}
                    </span>
                  </ButtonLink>
                </Link>
              )}
              {props.userApplied && (
                <StyledOfferBottomBarUserApplied>
                  <FontAwesomeIcon icon={faCheckCircle} />
                  <span aria-hidden="true">
                    {t('footer.bottomBar.alreadyApplied')}
                  </span>
                </StyledOfferBottomBarUserApplied>
              )}
            </>
          )}
          {!props.closed && (
            <>
              <Button
                variant="tertiaryInverseLightIcon"
                sizes="sm"
                id="bottombar-share-button"
                className="share-button"
                aria-label={t('share.share')}
                onClick={props.openShareModal}
              >
                <FontAwesomeIcon icon={faShareSquare} />
                <span aria-hidden="true">{t('share.share')}</span>
              </Button>

              {user && (
                <>
                  {!props.userApplied && (
                    <ButtonLink
                      variant="brandTalent"
                      sizes="lg"
                      id="bottombar-interested-button"
                      className="interested-button"
                      aria-label={t('footer.bottomBar.interested')}
                      href="#"
                      onClick={(e) => {
                        trackApplyClick();

                        if (props.handleApplyClick) {
                          props.handleApplyClick(e);
                        }
                      }}
                    >
                      <FontAwesomeIcon icon={faCheckCircle} />
                      <span aria-hidden="true">
                        {t('footer.bottomBar.interested')}
                      </span>
                    </ButtonLink>
                  )}
                  {props.userApplied && (
                    <StyledOfferBottomBarUserApplied>
                      <FontAwesomeIcon icon={faCheckCircle} />
                      <span aria-hidden="true">
                        {t('footer.bottomBar.alreadyApplied')}
                      </span>
                    </StyledOfferBottomBarUserApplied>
                  )}
                </>
              )}

              {!user && !loadingUser && (
                <ButtonLink
                  variant="brandTalent"
                  sizes="lg"
                  id="bottombar-interested-button"
                  className="interested-button"
                  aria-label={t('footer.bottomBar.interested')}
                  onClick={(e) => {
                    e.preventDefault();

                    trackApplyClick();

                    ServiceEventTracking.trackLandingCtaClicked('footer-job');

                    // Delay the redirect to give some time to the tracking to complete
                    setTimeout(() => {
                      const url = `${
                        window.location.origin
                      }/${locale}${getAppUrl(
                        APP_URLS.join,
                        locale
                      )}?locale=${locale}&offerId=${
                        props.id
                      }&source=footer-job&returnUrl=${returnUrl}`;

                      window.location.replace(url);
                    }, 300);
                  }}
                  href={`${getAppUrl(
                    APP_URLS.join,
                    locale
                  )}?locale=${locale}&offerId=${
                    props.id
                  }&source=footer-job&returnUrl=${returnUrl}`}
                >
                  <FontAwesomeIcon icon={faCheckCircle} />
                  <span aria-hidden="true">
                    {t('footer.bottomBar.interested')}
                  </span>
                </ButtonLink>
              )}
            </>
          )}
        </StyledOfferBottomBarActions>
      </StyledOfferBottomBarContainer>
      <StyledOfferBottomBarPageNav
        ref={nav}
        className={`${setActive}`}
        style={{ height: nav.current?.scrollHeight }}
      >
        <OfferBottomBarPageNav
          sections={props.sections}
          togglePageNav={togglePageNav}
        />
      </StyledOfferBottomBarPageNav>
    </StyledOfferBottomBar>
  );
};

export default OfferBottomBar;
